import React from 'react';
import { Card, CardContent, Typography, Box, LinearProgress } from '@mui/material';
import { colors, shadows } from '../theme/designSystem';

interface GaugeProps {
  title: string;
  value: number;
  min: number;
  max: number;
  targetMin?: number;
  targetMax?: number;
  unit: string;
}

const Gauge: React.FC<GaugeProps> = ({
  title,
  value,
  min,
  max,
  targetMin,
  targetMax,
  unit,
}) => {
  const numValue = Number(value) || 0;
  const percentage = ((numValue - min) / (max - min)) * 100;
  const isInRange =
    targetMin !== undefined && targetMax !== undefined
      ? numValue >= targetMin && numValue <= targetMax
      : true;

  const getColor = () => {
    if (!isInRange) return colors.status.error;
    if (percentage < 33) return colors.status.warning;
    if (percentage < 66) return colors.status.info;
    return colors.status.success;
  };

  return (
    <Card 
      sx={{ 
        height: '100%',
        borderRadius: '12px',
        border: `1px solid ${colors.neutral[200]}`,
        boxShadow: shadows.card,
        transition: 'box-shadow 0.3s ease',
        '&:hover': {
          boxShadow: shadows.cardHover,
        },
      }}
    >
      <CardContent sx={{ p: '20px' }}>
        <Typography 
          sx={{ 
            fontSize: '0.875rem',
            color: colors.neutral[500],
            fontWeight: 500,
            mb: 2,
          }}
        >
          {title}
        </Typography>
        <Box sx={{ my: 2 }}>
          <Typography 
            sx={{ 
              fontSize: '3rem',
              fontWeight: 700,
              color: colors.neutral[900],
              textAlign: 'center',
              lineHeight: 1,
              mb: 2,
            }}
          >
            {numValue.toFixed(1)}
            <Typography 
              component="span" 
              sx={{ 
                fontSize: '1.25rem',
                color: colors.neutral[500],
                fontWeight: 500,
                ml: 1,
              }}
            >
              {unit}
            </Typography>
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, Math.max(0, percentage))}
            sx={{
              height: 10,
              borderRadius: 5,
              backgroundColor: colors.neutral[200],
              '& .MuiLinearProgress-bar': {
                backgroundColor: getColor(),
                borderRadius: 5,
              },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography 
              sx={{ 
                fontSize: '0.75rem',
                color: colors.neutral[500],
              }}
            >
              {min} {unit}
            </Typography>
            <Typography 
              sx={{ 
                fontSize: '0.75rem',
                color: colors.neutral[500],
              }}
            >
              {max} {unit}
            </Typography>
          </Box>
          {targetMin !== undefined && targetMax !== undefined && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: '8px',
                backgroundColor: isInRange ? `${colors.status.success}15` : `${colors.status.error}15`,
              }}
            >
              <Typography 
                sx={{ 
                  fontSize: '0.75rem',
                  color: isInRange ? colors.status.success : colors.status.error,
                  fontWeight: 500,
                }}
              >
                Target: {targetMin} - {targetMax} {unit}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default Gauge;
